import { useCallback, useEffect, useState } from "react";
import {
  CATEGORY_IDS,
  CONSENT_OPEN_EVENT,
  GTM_ID,
  fullConsent,
  noConsent,
  recordConsent,
  storedConsent,
  type ConsentCategory,
  type ConsentChoices,
} from "../lib/gtm";
import { useTranslation } from "../i18n";
import { Modal } from "./Modal";
import { Button, cx } from "./ui";

/*
 * The cookie choice: a bar that asks, and a dialog that answers per category.
 *
 * Asked here as well as on the marketing site because the two are different
 * origins and local storage does not cross between them. Sharing the answer
 * would mean a cookie on the parent domain, written to record that somebody may
 * not want cookies.
 *
 * Three properties are load-bearing rather than stylistic, and the tests hold
 * each: refusing is exactly as easy as accepting, every optional category
 * starts off, and the choice is reversible from the account menu without
 * clearing browser data.
 */

/** A switch, local to this file — it is the only place in the app that needs one. */
function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative h-6 w-11 shrink-0 rounded-full border transition-colors duration-200 ease-smooth",
        checked ? "border-accent bg-accent" : "border-line-strong bg-raised",
      )}
    >
      <span
        className={cx(
          "absolute top-0.5 left-0.5 size-4 rounded-full transition-transform duration-200 ease-smooth",
          checked ? "translate-x-5 bg-white" : "bg-fg-subtle",
        )}
      />
    </button>
  );
}

function CategoryRow({
  id,
  choices,
  onChange,
}: {
  id: ConsentCategory | "necessary";
  choices: ConsentChoices;
  onChange: (next: ConsentChoices) => void;
}) {
  const { t } = useTranslation("consent");
  const copy = { title: t(`categories.${id}.title`), detail: t(`categories.${id}.detail`) };
  const optional = id !== "necessary";

  return (
    <div className="flex items-start gap-4 py-4">
      <details className="min-w-0 flex-1 [&_summary::-webkit-details-marker]:hidden">
        <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium text-fg">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="size-3.5 shrink-0 text-fg-subtle transition-transform duration-200 ease-smooth [details[open]_&]:rotate-90"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
          {copy.title}
        </summary>
        <p className="mt-2 pl-5 text-[13px] leading-5 text-fg-muted">{copy.detail}</p>
      </details>

      {optional ? (
        <Toggle
          checked={choices[id]}
          label={copy.title}
          onChange={(next) => onChange({ ...choices, [id]: next })}
        />
      ) : (
        <span className="shrink-0 pt-0.5 text-[13px] text-fg-subtle">{t("alwaysActive")}</span>
      )}
    </div>
  );
}

/**
 * The preferences dialog on its own, so the account menu can open it directly
 * without the banner being involved.
 */
export function ConsentPreferences({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}) {
  // Seeded from what is actually in force each time it opens, so somebody who
  // accepted and came back to change one category sees their own settings
  // rather than a fresh set of empty toggles.
  const { t } = useTranslation("consent");
  const [choices, setChoices] = useState<ConsentChoices>(() => storedConsent() ?? noConsent());

  useEffect(() => {
    if (open) setChoices(storedConsent() ?? noConsent());
  }, [open]);

  const commit = (next: ConsentChoices) => {
    recordConsent(next);
    onSaved?.();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("dialogTitle")}
      description={t("dialogDescription")}
      footer={
        <>
          {/* Refusal first and the same weight as acceptance. A decline styled
              as a footnote beside a solid accept is the pattern regulators name,
              and it is easy to reintroduce while tidying classes. */}
          <Button variant="secondary" onClick={() => commit(noConsent())}>
            {t("rejectAll")}
          </Button>
          <Button variant="secondary" onClick={() => commit(fullConsent())}>
            {t("acceptAll")}
          </Button>
          <Button variant="primary" onClick={() => commit(choices)}>
            {t("save")}
          </Button>
        </>
      }
    >
      <div className="divide-y divide-line">
        <CategoryRow id="necessary" choices={choices} onChange={setChoices} />
        {CATEGORY_IDS.map((id) => (
          <CategoryRow key={id} id={id} choices={choices} onChange={setChoices} />
        ))}
      </div>
    </Modal>
  );
}

/**
 * The bar, shown until the question has been answered once.
 *
 * Renders nothing when the build carries no container: there is nothing to
 * consent to, and asking would be asking about something that is not happening.
 */
export function ConsentBanner() {
  // Read at construction rather than in an effect, so a visitor who already
  // answered never sees the bar flash for one paint.
  const { t } = useTranslation("consent");
  const [answered, setAnswered] = useState(() => (GTM_ID ? storedConsent() !== null : true));
  const [open, setOpen] = useState(false);

  const decide = useCallback((choices: ConsentChoices) => {
    recordConsent(choices);
    setAnswered(true);
  }, []);

  /*
   * Reopened from the account menu, several levels away in the tree. One dialog
   * instance rather than a second one rendered beside the menu, so there is a
   * single answer to "what is currently in force".
   */
  useEffect(() => {
    const reopen = () => setOpen(true);
    window.addEventListener(CONSENT_OPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, reopen);
  }, []);

  if (GTM_ID === null) return null;

  return (
    <>
      {!answered && (
        <div
          role="region"
          aria-labelledby="consent-title"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface/95 backdrop-blur-md"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:gap-8">
            <div className="min-w-0 flex-1">
              <p id="consent-title" className="text-sm font-medium text-fg">
                {t("title")}
              </p>
              <p className="mt-1 text-[13px] leading-5 text-fg-muted">
                {t("body")}{" "}
                <a
                  href={`${import.meta.env.VITE_SITE_URL ?? ""}/legal/privacy`}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="text-accent underline underline-offset-2 hover:no-underline"
                >
                  {t("privacyPolicy")}
                </a>
              </p>
            </div>

            <div className="flex shrink-0 flex-wrap gap-3">
              <Button variant="ghost" onClick={() => setOpen(true)} className="flex-1 lg:flex-none">
                {t("settings")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => decide(noConsent())}
                className="flex-1 lg:flex-none"
              >
                {t("rejectAll")}
              </Button>
              <Button
                variant="primary"
                onClick={() => decide(fullConsent())}
                className="flex-1 lg:flex-none"
              >
                {t("acceptAll")}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConsentPreferences
        open={open}
        onClose={() => setOpen(false)}
        onSaved={() => setAnswered(true)}
      />
    </>
  );
}
